import { queryOptions, useQuery } from '@tanstack/react-query';

import { SECONDS_IN_HOUR } from '@cast/constants';

import { apiClient } from '@cast/infrastructure-api';

const CLUSTERS_TIMED_EFFICIENCY_QUERY_KEY = 'clusters-timed-efficiency';

export const getClusterEfficiencyTimeSeriesQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  stepSeconds,
  tourStepKey,
}: {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  stepSeconds: number;
  tourStepKey: string | null | undefined;
}) =>
  [
    CLUSTERS_TIMED_EFFICIENCY_QUERY_KEY,
    startTime,
    endTime,
    useListingPrices,
    stepSeconds,
    tourStepKey,
  ] as const;

export type UseClustersTimedEfficiencyQueryArgs = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  stepSeconds?: number;
  enabled?: boolean;
};

export const getClusterEfficiencyTimeSeriesQueryOptions = (
  {
    startTime,
    endTime,
    useListingPrices,
    stepSeconds = SECONDS_IN_HOUR,
    enabled = true,
  }: UseClustersTimedEfficiencyQueryArgs,
  tourStepKey: string | null | undefined
) =>
  queryOptions({
    queryKey: getClusterEfficiencyTimeSeriesQueryKey({
      startTime,
      endTime,
      useListingPrices,
      stepSeconds,
      tourStepKey,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getOrganizationClustersTimedEfficiency({
          startTime,
          endTime,
          useListingPrices,
          stepSeconds,
        });

      return data;
    },
    enabled,
  });

export const useClusterEfficiencyTimeSeriesQuery = (
  args: UseClustersTimedEfficiencyQueryArgs,
  tourStepKey: string | null | undefined = undefined
) => {
  const { data, isLoading, error, refetch } = useQuery(
    getClusterEfficiencyTimeSeriesQueryOptions(args, tourStepKey)
  );

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};
